import { toggleMobileNavIsOpen } from '@squareup/dex-data-shared-main-data-slices';
import { HeaderContainer } from '@squareup/dex-feature-shared-main-header-container';
import { PageContentsContainer } from '@squareup/dex-feature-shared-main-page-contents';
import { useTechRefDispatch } from '@squareup/dex-store-access-dex-tech-main-dispatch';
import { useTechRefStore } from '@squareup/dex-store-access-dex-tech-main-store';
import {
  selectErrorState,
  useSelectMobileNavState,
} from '@squareup/dex-store-access-shared-main-selectors';
import { useSharedStore } from '@squareup/dex-store-access-shared-main-store';
import { ReferenceNavigationContainer } from '@squareup/dex-tech-reference-navigation';
import { ReferenceObjectModalProvider } from '@squareup/dex-tech-reference-properties';
import { loadGraphEntitiesIntoView } from '@squareup/dex-tech-store-access-actions';
import {
  selectGraphEntitiesInView,
  selectAnyGraphInViewError,
} from '@squareup/dex-tech-store-access-selectors';
import { ServerAppInitResults } from '@squareup/dex-types-shared-app';
import { Flags } from '@squareup/dex-types-shared-developer-api';
import { UserInfo } from '@squareup/dex-types-shared-developer-data-api';
import { NullableClassName } from '@squareup/dex-types-shared-ui';
import { Footer, SuspendOnQueryAction } from '@squareup/dex-ui';
import { PageLayout } from '@squareup/dex-ui-page-layout';
import {
  MobileNavDomManager,
  MobileNavShifter,
  MobileNavShifterRenderProps,
  MobileNavVeil,
} from '@squareup/dex-ui-shared-mobile-nav';
import { isServerContext } from '@squareup/dex-utils-environment';
import {
  initialStateToResponse,
  settledResultToStoreStateNode,
} from '@squareup/dex-utils-error';
import { withSqAvtPagesDataFetchMiddleware } from '@squareup/dex-utils-shared-next-pages-data-fetch-middleware';
import {
  usePathname,
  useParams,
  ROUTER_NOT_READY,
} from '@squareup/dex-utils-shared-routing-hooks';
import { runAndRecordFrameworkMetrics } from '@squareup/dex-utils-shared-server-framework-metrics';
import { GetServerSidePropsContext, NextPageContext } from 'next';
import React, {
  FunctionComponent,
  ReactNode,
  useCallback,
  useMemo,
} from 'react';
import { v4 } from 'uuid';

import { configureApp } from './app-config';
import { initializeApp as initializeServerApp } from './server-app-init';
import {
  setupS2sFetch,
  setupServerLogger,
  setupServerMetrics,
} from './server-module-setup';

const ERROR_CONTEXT = 'square version page';

type PageContentProps = {
  children?: ReactNode;
};

const Navigation = (props: NullableClassName) => {
  const techRefStore = useTechRefStore();
  const sharedStore = useSharedStore();
  const { pathName, isReady } = usePathname();
  const asPath = isReady ? pathName : ROUTER_NOT_READY;
  const params = useParams();

  const {
    square_version,
    enum: enumValue,
    object,
    api,
    endpoint,
    webhook,
  } = params ?? ({} as never);

  const query = useMemo(
    () => ({
      square_version: square_version as string,
      enum: enumValue as string,
      object: object as string,
      api: api as string,
      endpoint: endpoint as string,
      webhook: webhook as string,
    }),
    [square_version, enumValue, object, api, endpoint, webhook]
  );

  const loadingSelector = useCallback(() => {
    return selectGraphEntitiesInView(techRefStore.getState());
  }, [techRefStore]);

  const errorSelector = useCallback(() => {
    const error = selectErrorState(sharedStore.getState());
    return Boolean(error.global || error.page);
  }, [sharedStore]);

  return (
    <SuspendOnQueryAction
      loadingSelector={loadingSelector}
      action={loadGraphEntitiesIntoView}
      errorSelector={errorSelector}
      query={query}
    >
      <ReferenceNavigationContainer
        className={props.className}
        pathname={asPath}
      />
    </SuspendOnQueryAction>
  );
};

const Header = (props: NullableClassName) => {
  const techRefStore = useTechRefStore();
  const sharedStore = useSharedStore();
  const params = useParams();
  const {
    square_version,
    enum: enumValue,
    object,
    api,
    endpoint,
    webhook,
  } = params ?? ({} as never);

  const query = useMemo(
    () => ({
      square_version: square_version as string,
      enum: enumValue as string,
      object: object as string,
      api: api as string,
      endpoint: endpoint as string,
      webhook: webhook as string,
    }),
    [square_version, enumValue, object, api, endpoint, webhook]
  );

  const loadingSelector = useCallback(() => {
    return selectGraphEntitiesInView(techRefStore.getState());
  }, [techRefStore]);

  const errorSelector = useCallback(() => {
    const error = selectErrorState(sharedStore.getState());
    return Boolean(error.global || error.page);
  }, [sharedStore]);

  return (
    <SuspendOnQueryAction
      loadingSelector={loadingSelector}
      action={loadGraphEntitiesIntoView}
      errorSelector={errorSelector}
      query={query}
    >
      <HeaderContainer
        className={props.className}
        searchDomain={'apiReference'}
      />
    </SuspendOnQueryAction>
  );
};

const PageContentWrapper = ({ children }: PageContentProps) => {
  const techRefStore = useTechRefStore();
  const sharedStore = useSharedStore();
  const params = useParams();

  const {
    square_version,
    enum: enumValue,
    object,
    api,
    endpoint,
    webhook,
  } = params ?? ({} as never);

  const query = useMemo(
    () => ({
      square_version: square_version as string,
      enum: enumValue as string,
      object: object as string,
      api: api as string,
      endpoint: endpoint as string,
      webhook: webhook as string,
    }),
    [square_version, enumValue, object, api, endpoint, webhook]
  );

  const loadingSelector = useCallback(() => {
    return selectGraphEntitiesInView(techRefStore.getState());
  }, [techRefStore]);

  const errorSelector = useCallback(() => {
    const error = selectErrorState(sharedStore.getState());
    return Boolean(error.global || error.page);
  }, [sharedStore]);

  return (
    <>
      <SuspendOnQueryAction
        loadingSelector={loadingSelector}
        action={loadGraphEntitiesIntoView}
        errorSelector={errorSelector}
        query={query}
      >
        {children}
      </SuspendOnQueryAction>
    </>
  );
};

const CommonPageContents = () => {
  const { isOpen } = useSelectMobileNavState();
  const dispatch = useTechRefDispatch();
  const onClick = useCallback(() => {
    dispatch(toggleMobileNavIsOpen());
  }, [dispatch]);

  return (
    <>
      <MobileNavVeil isOpen={isOpen} onClick={onClick} />
      <MobileNavDomManager isOpen={isOpen} onCloseAction={onClick} />
    </>
  );
};

const FooterContents = () => {
  const { isOpen } = useSelectMobileNavState();

  const renderFooter: FunctionComponent<MobileNavShifterRenderProps> = ({
    className: mobileShifterClassName,
  }) => {
    return <Footer className={mobileShifterClassName}></Footer>;
  };

  return (
    <MobileNavShifter
      isMobileNavOpen={isOpen}
      render={renderFooter}
    ></MobileNavShifter>
  );
};

/**
 * This page is a common component which is to be sub classed. Its not indended to be used as a stand alone page.
 * It provides the common loading and layout funcitonality used by all pages in the tech ref. Its expected that a
 * subclass is to override
 *
 * getPageContent - returns a call to the SquareVersionPage getPageContent providing the children and hydration props. This allows the render
 *                  phase to work properly across SSR + client hydrate. This is needed because the tech ref data must be loaded twice due to its size
 *                  as it cannot be inlined.
 *
 * getInitialProps - returns a call to the SquareVersionPage getInitialProps providing the server context and hydration props. This allows
 *                   SSR to propertly render.
 */
class SquareVersionPage extends React.Component<void> {
  public static hydrationProps = {
    hydrationAction: loadGraphEntitiesIntoView,
    hydrationLoadingSelector: selectGraphEntitiesInView,
  };

  public static routeHandlerName: string | undefined;

  public override render(): React.ReactNode {
    return (
      <PageLayout
        Navigation={Navigation}
        Header={Header}
        Footer={FooterContents}
        contents={
          <>
            <CommonPageContents />
            {this.getPageContent({})}
          </>
        }
      ></PageLayout>
    );
  }

  protected getPageContent({ children }: PageContentProps): ReactNode {
    return (
      <PageContentWrapper>
        <ReferenceObjectModalProvider>
          <PageContentsContainer returnHref="/reference/square">
            {children}
          </PageContentsContainer>
        </ReferenceObjectModalProvider>
      </PageContentWrapper>
    );
  }

  /**
   * Gets the props for the page.
   * This fuction gets called for each page request.
   * Which includes server side and subsequent client side navigations.
   */
  public static getInitialProps(ctx: GetServerSidePropsContext) {
    withSqAvtPagesDataFetchMiddleware(ctx);

    return runAndRecordFrameworkMetrics(
      { group: 'api-reference', handler: this.routeHandlerName },
      async () => {
        const requestId = v4();

        if (isServerContext()) {
          await setupServerMetrics();
          await setupS2sFetch();
          await setupServerLogger();
        }

        const {
          error: configError,
          isLoading: configIsLoading,
          store,
        } = configureApp({ requestId });

        // Config error: There is no infra here.
        if (configError || configIsLoading) {
          // TODO: Redirect to standard error page with error meta.
          return { responseCode: 500, returnVal: {} };
        }

        const initResults = [];

        if (isServerContext()) {
          const serverInitProps = {
            headers: ctx.req?.headers,
            cookies: ctx.req?.cookies,
            requestId,
          };
          initResults.push(initializeServerApp(serverInitProps));
        }

        initResults.push(store.dispatch(loadGraphEntitiesIntoView(ctx.query)));

        const [serverResults] = await Promise.allSettled(initResults);

        const serverInitResults: ServerAppInitResults<UserInfo, Flags> = {
          ...((serverResults &&
            settledResultToStoreStateNode(serverResults, ERROR_CONTEXT)) || {
            data: {},
          }),
          requestId,
        };

        const response = initialStateToResponse(
          ctx as unknown as NextPageContext,
          store,
          serverInitResults,
          selectAnyGraphInViewError
        );

        return {
          responseCode: ctx?.res?.statusCode || 200,
          returnVal: response,
        };
      }
    );
  }
}
export { SquareVersionPage, type PageContentProps };
